// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { AddressAutofill } from '@mapbox/search-js-react';
import { ChangeEvent } from 'react';
import { useTranslate } from '@tolgee/react';
import Card from '@app/components/card/card.tsx';
import CardHeader from '@app/components/card/card-header.tsx';
import CardContent from '@app/components/card/card-content.tsx';
import TextInput from '@app/components/form/input/text-input.tsx';
import FormItem from '@app/components/form/form-item.tsx';
import FormLayout from '@app/components/form/form-layout.tsx';
import DualItem from '@app/components/form/dual-form-item.tsx';
import { BranchItemFragment } from '@app/graphql/types/graphql.ts';

export const COUNTRY_LOCALE_MAP: Record<string, { code: string; language: string }> = {
  Germany: { code: 'DE', language: 'de' },
  Austria: { code: 'AT', language: 'de' },
  Switzerland: { code: 'CH', language: 'de' },
  France: { code: 'FR', language: 'fr' },
  Italy: { code: 'IT', language: 'it' },
  Spain: { code: 'ES', language: 'es' },
  Netherlands: { code: 'NL', language: 'nl' },
  Belgium: { code: 'BE', language: 'nl' },
  Poland: { code: 'PL', language: 'pl' },
  'Czech Republic': { code: 'CZ', language: 'cs' },
  Czechia: { code: 'CZ', language: 'cs' },
  Denmark: { code: 'DK', language: 'da' },
  Sweden: { code: 'SE', language: 'sv' },
  Norway: { code: 'NO', language: 'no' },
  Finland: { code: 'FI', language: 'fi' },
  Portugal: { code: 'PT', language: 'pt' },
  'United Kingdom': { code: 'GB', language: 'en' },
  Ireland: { code: 'IE', language: 'en' },
  Luxembourg: { code: 'LU', language: 'de' },
  Greece: { code: 'GR', language: 'el' },
  Hungary: { code: 'HU', language: 'hu' },
  Romania: { code: 'RO', language: 'ro' },
  Bulgaria: { code: 'BG', language: 'bg' },
  Croatia: { code: 'HR', language: 'hr' },
  Slovenia: { code: 'SI', language: 'sl' },
  Slovakia: { code: 'SK', language: 'sk' },
  'United States': { code: 'US', language: 'en' },
  USA: { code: 'US', language: 'en' },
  Canada: { code: 'CA', language: 'en' },
};

const AutofillAddressInput = (props: {
  values: {
    streetAddress: string;
    streetAddress2: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    // addressCountry: string;
  };
  streetAddress: (value: string) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  error?: Record<string, string>;
  branch: BranchItemFragment;
}) => {
  const accessToken = import.meta.env.VITE_MAP_BOX as string;
  const { t } = useTranslate();

  const country = COUNTRY_LOCALE_MAP[props.branch.addressCountry] ?? { code: 'DE', language: 'de' };
  return (
    <AddressAutofill
      accessToken={accessToken}
      options={{
        country: country.code,
        language: country.language,
      }}
    >
      <Card className="card checkout-form__address-card">
        <CardHeader title={t('online-order.fill-contact-page.form-input.delivery.address-title', 'Delivery address')} />
        <CardContent className="checkout-form__address-content">
          <FormLayout className="checkout-form__address-layout">
            <FormItem title={t('online-order.fill-contact-page.form-input.delivery.address-1.label', 'Address line 1')} className="checkout-form__field checkout-form__field--address-line-1">
              <TextInput
                label="streetAddress"
                type="text"
                name="streetAddress"
                autoComplete="address-line1"
                className="checkout-form__input checkout-form__input--address-line-1 mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder={t(
                  'online-order.fill-contact-page.form-input.delivery.address-1.placeholder',
                  'Address line 1'
                )}
                value={props.values.streetAddress}
                error={props.error?.streetAddress}
                onChange={(e) => {
                  props.streetAddress(e.target.value);
                }}
              />
            </FormItem>

            <FormItem title={t('online-order.fill-contact-page.form-input.delivery.address-2.label', 'Address line 2')} className="checkout-form__field checkout-form__field--address-line-2">
              <TextInput
                label="streetAddress2"
                type="text"
                name="streetAddress2"
                autoComplete="address-line2"
                className="checkout-form__input checkout-form__input--address-line-2 mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder={t(
                  'online-order.fill-contact-page.form-input.delivery.address-2.placeholder',
                  'Address line 2'
                )}
                value={props.values.streetAddress2}
                error={props.error?.streetAddress2}
                onChange={props.onChange}
              />
            </FormItem>

            <DualItem className="checkout-form__dual-field">
              <FormItem title={t('online-order.fill-contact-page.form-input.delivery.city.label', 'City')} className="checkout-form__field checkout-form__field--city">
                <TextInput
                  type="text"
                  name="addressLocality"
                  autoComplete="address-level2"
                  className="checkout-form__input checkout-form__input--city mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder={t('online-order.fill-contact-page.form-input.delivery.city.placeholder', 'City')}
                  value={props.values.addressLocality}
                  error={props.error?.addressLocality}
                  onChange={props.onChange}
                />
              </FormItem>

              <FormItem
                title={t('online-order.fill-contact-page.form-input.delivery.postal-code.label', 'Postal code')}
                className="checkout-form__field checkout-form__field--postal-code"
              >
                <TextInput
                  type="text"
                  name="postalCode"
                  autoComplete="postal-code"
                  className="checkout-form__input checkout-form__input--postal-code mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder={t('online-order.fill-contact-page.form-input.delivery.postal-code.placeholder', 'Zip')}
                  value={props.values.postalCode}
                  error={props.error?.postalCode}
                  onChange={props.onChange}
                />
              </FormItem>
            </DualItem>

            {/*<div>*/}
            {/*  <label htmlFor="state" className="block text-sm font-medium leading-6 text-gray-900">*/}
            {/*    State*/}
            {/*  </label>*/}
            {/*  <input*/}
            {/*    type="text"*/}
            {/*    name="addressRegion"*/}
            {/*    autoComplete="address-level1"*/}
            {/*    className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"*/}
            {/*    placeholder="State"*/}
            {/*    value={props.values.addressRegion}*/}
            {/*    onChange={props.onChange}*/}
            {/*  />*/}

            {/*  <ErrorLabel error={props.error?.addressRegion} />*/}
            {/*</div>*/}
          </FormLayout>
        </CardContent>
      </Card>
    </AddressAutofill>
  );
};

export default AutofillAddressInput;
